import { APIRequestError } from '@/services/api';
import { createCheckoutSession } from '@/services/checkout';
import { findSubscriptionProduct, hasExternalSuccessPage } from '@/services/subscription-products';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import { ERROR_MESSAGES } from '@/utils/errorMessages';
import type { SubscriptionID, SubscriptionPriceProduct, SubscriptionType, User } from '@/models';

/**
 * Verketteter Checkout: mehrere Pakete aus EINEM Link werden als aufeinanderfolgende
 * Stripe-Checkouts gebucht (z.B. Community-Abo + Ausbildung als Einmalzahlung — das Backend
 * lehnt gemischte Warenkörbe in einer Session ab, weil Stripe pro Session nur einen Modus kennt).
 *
 * Der Fortschritt lebt in `sessionStorage` (Stripe-Redirects bleiben im selben Tab) und wird
 * positional über ein Schritt-Array mit Status verfolgt — nie rückwärts, damit ein verzögerter
 * Aktivierungs-Webhook nach Schritt 1 weder Paket 1 erneut bucht noch Paket 2 überspringt.
 *
 * Die Ergebnis-Seiten (`/checkout/success`, `/checkout/cancel`) ordnen sich über die
 * `subscriptionId` aus der Backend-URL einem Schritt zu; fremde IDs (z.B. Kauf über /pricing)
 * werden ignoriert, liegen gebliebene Ketten sind damit harmlos.
 */

export type ChainStepStatus = 'pending' | 'started' | 'done' | 'skipped';
export type CheckoutChainSource = 'registration' | 'checkout-link' | 'register-link-loggedin';

export interface ChainStep {
  plan: SubscriptionType;
  subscriptionId: SubscriptionID | null;
  status: ChainStepStatus;
}

export interface CheckoutChain {
  steps: ChainStep[];
  coupon?: string;
  referrerCode?: string;
  source: CheckoutChainSource;
  /** Success-Seiten (per subscriptionId aus der URL, daher plain string), die bereits automatisch weitergeleitet haben. */
  autoContinuedFor: string[];
  ts: number;
}

/** Paket-Auswahl aus einem Register-Link, die den Login/Registrierungs-Umweg überleben muss. */
export interface PendingCheckout {
  plans: SubscriptionType[];
  coupon?: string;
  referrerCode?: string;
}

export interface ChainProgress {
  chain: CheckoutChain;
  /** Schritt, zu dem die aktuelle Ergebnis-Seite gehört. */
  stepIndex: number;
  /** Nächster noch offener Schritt oder -1, wenn die Kette abgeschlossen ist. */
  nextStepIndex: number;
  total: number;
  /** Mindestens ein Paket wurde bereits gebucht. */
  hasCompletedStep: boolean;
}

export type StartChainResult = 'REDIRECTING' | 'ALL_OWNED';
export type ContinueChainResult = 'REDIRECTING' | 'DONE';

const PENDING_KEY = 'pendingCheckout';
const CHAIN_KEY = 'checkout_chain';
/** Lebensdauer einer Stripe-Checkout-Session. */
const CHAIN_TTL_MS = 24 * 60 * 60 * 1000;

/** Noch nicht abgeschlossen: offen oder bei Stripe gestartet (Session ggf. verlassen, ohne Cancel-Seite). */
function isOpenStep(step: ChainStep): boolean {
  return step.status === 'pending' || step.status === 'started';
}

// ============================================
// URL-Parameter
// ============================================

/** Optionaler Query-Parameter: getrimmt, leer -> undefined. */
export function parseOptionalParam(searchParams: URLSearchParams, name: string): string | undefined {
  const value = searchParams.get(name);
  if (value === null) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed !== '' ? trimmed : undefined;
}

/**
 * Liest alle Pakete aus `?plan=A&plan=B` bzw. `?plan=A,B` in Link-Reihenfolge.
 *
 * Verworfen werden: unbekannte Typen, Duplikate nach SKU und nach Produktfamilie (das Backend
 * lehnt gleiche Familien nur innerhalb EINES Requests ab, über zwei Checkouts käme ein Doppel-Abo
 * durch) sowie Pakete mit externer Success-URL, die nicht an letzter Stelle stehen.
 * Typen ohne Stripe-Produkt bleiben bewusst drin (Verhalten der Ein-Paket-Links unverändert):
 * der Checkout meldet das dann als „aktuell nicht verfügbar“.
 */
export function parsePlanParams(searchParams: URLSearchParams, products: SubscriptionPriceProduct[]): SubscriptionType[] {
  const raw = searchParams
    .getAll('plan')
    .flatMap(value => value.split(','))
    .map(value => value.trim())
    .filter(value => value !== '');

  const result: SubscriptionType[] = [];
  const seenTypes = new Set<SubscriptionType>();
  const seenFamilies = new Set<string>();

  for (const type of raw) {
    const product = findSubscriptionProduct(products, type);
    if (product === undefined) {
      continue;
    }
    if (seenTypes.has(type)) {
      continue;
    }
    if (product.productType !== null && seenFamilies.has(product.productType)) {
      continue;
    }
    seenTypes.add(type);
    if (product.productType !== null) {
      seenFamilies.add(product.productType);
    }
    result.push(type);
  }

  return result.filter((type, index) => {
    if (index === result.length - 1) {
      return true;
    }
    return !hasExternalSuccessPage(findSubscriptionProduct(products, type));
  });
}

// ============================================
// Besitz
// ============================================

/**
 * Bereits besessen: exakte SKU aktiv ODER ein Paket derselben Produktfamilie aktiv.
 * Bewusst NICHT die Feature-Abdeckung aus `subscription-status.ts` — für die Kauf-Filterung
 * wäre das zu aggressiv (Cashflow wäre für Community-Mitglieder "abgedeckt").
 */
export function isPlanOwned(user: User, product: SubscriptionPriceProduct): boolean {
  if (user.activeSubscriptionTypes.includes(product.type)) {
    return true;
  }
  return product.productType !== null && user.activeSubscriptionProductTypes.includes(product.productType);
}

export function filterUnownedPlans(user: User, plans: SubscriptionType[], products: SubscriptionPriceProduct[]): SubscriptionType[] {
  return plans.filter(plan => {
    const product = findSubscriptionProduct(products, plan);
    return product !== undefined && !isPlanOwned(user, product);
  });
}

/** Nutzer-Meldung, wenn kein Paket des Links mehr zu kaufen ist. */
export function getAllOwnedMessage(planCount: number): string {
  return planCount > 1 ? 'Du hast diese Pakete bereits aktiv.' : ERROR_MESSAGES.SUBSCRIPTION_ALREADY_EXISTS;
}

// ============================================
// Pending-State (Registrierung / Login)
// ============================================

export function savePendingCheckout(pending: PendingCheckout): void {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

/** Liest die gemerkte Auswahl und löscht sie — sie darf nur einen Auto-Checkout auslösen. */
export function takePendingCheckout(): PendingCheckout | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PendingCheckout>;
    if (!Array.isArray(parsed.plans)) {
      return null;
    }
    const plans = parsed.plans.filter((plan): plan is string => typeof plan === 'string' && plan.trim() !== '');
    if (plans.length === 0) {
      return null;
    }
    return {
      plans: plans,
      coupon: typeof parsed.coupon === 'string' && parsed.coupon !== '' ? parsed.coupon : undefined,
      referrerCode: typeof parsed.referrerCode === 'string' && parsed.referrerCode !== '' ? parsed.referrerCode : undefined,
    };
  } catch {
    return null;
  }
}

// ============================================
// Chain-State (zwischen den Stripe-Redirects)
// ============================================

export function readChain(): CheckoutChain | null {
  const raw = sessionStorage.getItem(CHAIN_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CheckoutChain;
    if (!Array.isArray(parsed.steps) || parsed.steps.length === 0 || typeof parsed.ts !== 'number') {
      return null;
    }
    if (Date.now() - parsed.ts > CHAIN_TTL_MS) {
      sessionStorage.removeItem(CHAIN_KEY);
      return null;
    }
    if (!Array.isArray(parsed.autoContinuedFor)) {
      parsed.autoContinuedFor = [];
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveChain(chain: CheckoutChain): void {
  sessionStorage.setItem(CHAIN_KEY, JSON.stringify(chain));
}

export function clearChain(): void {
  sessionStorage.removeItem(CHAIN_KEY);
}

function findNextOpenIndex(chain: CheckoutChain): number {
  return chain.steps.findIndex(isOpenStep);
}

function buildProgress(chain: CheckoutChain, stepIndex: number): ChainProgress {
  return {
    chain: chain,
    stepIndex: stepIndex,
    nextStepIndex: findNextOpenIndex(chain),
    total: chain.steps.length,
    hasCompletedStep: chain.steps.some(step => step.status === 'done'),
  };
}

/**
 * Für `/checkout/success`: markiert den zur `subscriptionId` gehörenden Schritt als erledigt.
 * Ist danach kein Schritt mehr offen, wird die Kette gelöscht. `null`, wenn die ID zu keiner
 * Kette gehört (normaler Einzelkauf).
 */
export function completeChainStep(subscriptionId: string | null): ChainProgress | null {
  const chain = readChain();
  if (chain === null || subscriptionId === null) {
    return null;
  }
  const stepIndex = chain.steps.findIndex(step => step.subscriptionId === subscriptionId);
  if (stepIndex === -1) {
    return null;
  }
  chain.steps[stepIndex].status = 'done';
  const progress = buildProgress(chain, stepIndex);
  if (progress.nextStepIndex === -1) {
    clearChain();
  } else {
    saveChain(chain);
  }
  return progress;
}

/**
 * Für `/checkout/cancel`: setzt den abgebrochenen Schritt zurück auf offen, damit er über
 * {@link continueChain} erneut gestartet werden kann. Die `subscriptionId` bleibt am Schritt,
 * damit ein Reload der Cancel-Seite ihn weiterhin zuordnen kann (idempotent); beim Neustart
 * überschreibt {@link launchStep} sie mit der neuen Session. `null`, wenn die ID zu keiner Kette gehört.
 */
export function abortChainStep(subscriptionId: string | null): ChainProgress | null {
  const chain = readChain();
  if (chain === null || subscriptionId === null) {
    return null;
  }
  const stepIndex = chain.steps.findIndex(step => step.subscriptionId === subscriptionId);
  if (stepIndex === -1) {
    return null;
  }
  chain.steps[stepIndex].status = 'pending';
  saveChain(chain);
  return buildProgress(chain, stepIndex);
}

/** Merkt sich, dass die Success-Seite dieser Subscription bereits automatisch weitergeleitet hat. */
export function markAutoContinued(chain: CheckoutChain, subscriptionId: string): void {
  if (!chain.autoContinuedFor.includes(subscriptionId)) {
    chain.autoContinuedFor.push(subscriptionId);
    saveChain(chain);
  }
}

// ============================================
// Start / Fortsetzen
// ============================================

export interface StartChainParams {
  user: User;
  plans: SubscriptionType[];
  coupon: string | undefined;
  referrerCode: string | undefined;
  source: CheckoutChainSource;
  products: SubscriptionPriceProduct[];
}

/**
 * Startet eine neue Kette für die noch nicht besessenen Pakete und leitet zum ersten
 * Stripe-Checkout weiter. `ALL_OWNED`, wenn nichts mehr zu kaufen ist — der Aufrufer
 * ist für das Nutzer-Feedback zuständig. Wirft `APIRequestError` bei Fehlschlag.
 */
export async function startCheckoutChain(params: StartChainParams): Promise<StartChainResult> {
  const remaining = filterUnownedPlans(params.user, params.plans, params.products);
  if (remaining.length === 0) {
    return 'ALL_OWNED';
  }

  const chain: CheckoutChain = {
    steps: remaining.map(plan => ({ plan: plan, subscriptionId: null, status: 'pending' })),
    coupon: params.coupon,
    referrerCode: params.referrerCode,
    source: params.source,
    autoContinuedFor: [],
    ts: Date.now(),
  };

  const result = await continueChain(params.user, chain, params.products);
  // Alles per 409 übersprungen -> für den Aufrufer dasselbe wie "alles bereits vorhanden"
  return result === 'DONE' ? 'ALL_OWNED' : 'REDIRECTING';
}

/**
 * Startet den nächsten offenen Schritt der Kette (streng positional). Ein bereits bei Stripe
 * gestarteter, aber verlassener Schritt bekommt eine neue Session. Inzwischen besessene Pakete
 * werden übersprungen. `DONE`, wenn nichts mehr offen ist — die Kette ist dann gelöscht.
 */
export async function continueChain(user: User, chain: CheckoutChain, products: SubscriptionPriceProduct[]): Promise<ContinueChainResult> {
  for (let index = 0; index < chain.steps.length; index++) {
    const step = chain.steps[index];
    if (!isOpenStep(step)) {
      continue;
    }
    const product = findSubscriptionProduct(products, step.plan);
    if (product !== undefined && isPlanOwned(user, product)) {
      step.status = 'skipped';
      saveChain(chain);
      continue;
    }
    const launched = await launchStep(user, chain, index);
    if (launched) {
      return 'REDIRECTING';
    }
  }
  clearChain();
  return 'DONE';
}

/**
 * Legt die Checkout-Session für einen Schritt an und leitet weiter. Write-ahead: der Schritt
 * wird VOR dem Request als gestartet gespeichert und bei Fehler zurückgesetzt.
 * `false`, wenn das Backend das Paket als bereits aktiv meldet (z.B. in einem anderen Tab
 * gekauft) — der Schritt gilt dann als übersprungen. Andere Fehler werden weitergeworfen.
 */
async function launchStep(user: User, chain: CheckoutChain, index: number): Promise<boolean> {
  const step = chain.steps[index];
  step.status = 'started';
  saveChain(chain);

  analytics.track(AnalyticsEvents.CHECKOUT_STARTED, {
    productType: step.plan,
    plans: chain.steps.map(s => s.plan),
    chainStep: index + 1,
    chainTotal: chain.steps.length,
    source: chain.source,
    coupon: chain.coupon ?? null,
    referrerCode: chain.referrerCode ?? null,
  });

  try {
    const response = await createCheckoutSession(user.id, step.plan, chain.coupon, chain.referrerCode);
    step.subscriptionId = response.subscription.id;
    saveChain(chain);
    window.location.href = response.checkoutUrl;
    return true;
  } catch (error) {
    if (error instanceof APIRequestError && error.errorcode === 'SUBSCRIPTION_ALREADY_EXISTS') {
      step.status = 'skipped';
      saveChain(chain);
      return false;
    }
    step.status = 'pending';
    saveChain(chain);
    throw error;
  }
}
